import { useEffect, useState } from "react";
import http, { formatApiErrorDetail } from "@/api";
import { FileText, ExternalLink, Check, AlertTriangle, XCircle, Clock } from "lucide-react";
import Modal from "@/components/Modal";

const STATUS_META = {
  pending: { label: "Pendiente", color: "#7C7560", bg: "#EEE4CE", icon: Clock },
  valid: { label: "Válido", color: "#0F5132", bg: "#D1F2E0", icon: Check },
  expired: { label: "Vencido", color: "#8A5A00", bg: "#FCEFC7", icon: AlertTriangle },
  invalid: { label: "No corresponde", color: "#7F1D1D", bg: "#FCD3D3", icon: XCircle },
  under_review: { label: "En revisión", color: "#1B225E", bg: "#DCE0FF", icon: Clock },
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ReviewModal({ doc, onClose, onSaved }) {
  const [score, setScore] = useState(doc.evaluator_score ?? 8);
  const [status, setStatus] = useState(doc.evaluator_status || doc.status || "valid");
  const [notes, setNotes] = useState(doc.evaluator_notes || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    setBusy(true); setErr("");
    try {
      await http.put(`/documents/${doc.id}/review`, {
        evaluator_score: Number(score),
        evaluator_status: status,
        evaluator_notes: notes || null,
      });
      onSaved?.();
      onClose();
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <Modal testId="doc-review-modal" align="center" onClose={onClose} closeOnOverlay>
      <div className="sketch-border bg-[var(--cream)] p-6 max-w-2xl w-full">
        <div className="font-hand text-lg text-[var(--lala-blue)]">revisión asistida</div>
        <h3 className="font-display text-2xl">{doc.criterion_label}</h3>
        <p className="text-xs text-[var(--ink-soft)]">{doc.filename} · {(doc.size / 1024).toFixed(1)} KB</p>

        <div className="mt-4">
          <label className="text-xs uppercase tracking-wider text-[var(--ink-soft)] font-bold">Score del evaluador</label>
          <div className="flex items-center gap-3 mt-1">
            <input type="range" min="0" max="10" step="0.1" value={score}
              onChange={(e) => setScore(e.target.value)}
              className="flex-1 accent-[var(--lala-blue)]"
              data-testid="doc-review-score" />
            <span className="font-display text-3xl text-[var(--lala-blue)]">{Number(score).toFixed(1)}</span>
          </div>
        </div>

        <div className="mt-4">
          <label className="text-xs uppercase tracking-wider text-[var(--ink-soft)] font-bold">Estatus final</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="input-field mt-1" data-testid="doc-review-status">
            <option value="valid">✅ Válido</option>
            <option value="expired">⚠️ Vencido</option>
            <option value="invalid">❌ No corresponde</option>
            <option value="under_review">🔄 En revisión adicional</option>
          </select>
        </div>

        <div className="mt-4">
          <label className="text-xs uppercase tracking-wider text-[var(--ink-soft)] font-bold">Notas (opcional)</label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="input-field mt-1" rows={3} data-testid="doc-review-notes" />
        </div>

        {err && <div className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">{err}</div>}

        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="btn-ghost text-sm">Cancelar</button>
          <button onClick={save} disabled={busy} className="btn-primary text-sm" data-testid="doc-review-save">
            <Check size={14} /> {busy ? "Guardando…" : "Confirmar revisión"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default function SupplierDocumentsPanel({ supplierId }) {
  const [docs, setDocs] = useState([]);
  const [reviewing, setReviewing] = useState(null);
  const [err] = useState("");

  const reload = async () => {
    const { data } = await http.get(`/suppliers/${supplierId}/documents`);
    setDocs(data);
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [supplierId]);

  const openDoc = async (id) => {
    const res = await fetch(`${API}/documents/${id}/download`, {
      credentials: "include", // Send cookies with request
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank", "noopener");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  return (
    <div className="sketch-border bg-[var(--cream-soft)] p-6" data-testid="supplier-docs-panel">
      <div className="font-hand text-xl text-[var(--lala-blue)]">documentos del proveedor</div>
      <h3 className="font-display text-2xl">Evidencia documental</h3>
      <p className="text-xs text-[var(--ink-soft)] mb-4">
        Revisa cada documento y confirma su estatus y score.
      </p>

      {err && <div className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">{err}</div>}

      {docs.length === 0 ? (
        <div className="text-sm text-[var(--ink-soft)] italic py-4 text-center">El proveedor aún no ha subido documentos.</div>
      ) : (
        <ul className="divide-y divide-[var(--line)]" data-testid="supplier-docs-list">
          {docs.map((d) => {
            const meta = STATUS_META[d.status] || STATUS_META.pending;
            const Icon = meta.icon;
            return (
              <li key={d.id} className="py-3" data-testid={`supplier-doc-${d.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold">{d.criterion_label}</div>
                    <button onClick={() => openDoc(d.id)} className="text-sm text-[var(--ink)] hover:text-[var(--lala-blue)] hover:underline inline-flex items-center gap-1 mt-0.5">
                      <FileText size={14} /> {d.filename} <ExternalLink size={11} />
                    </button>
                    <div className="text-xs text-[var(--ink-soft)] mt-0.5">
                      Subido {new Date(d.uploaded_at).toLocaleDateString("es-MX")} · {(d.size / 1024).toFixed(1)} KB
                    </div>
                    {d.evaluator_status && (
                      <div className="text-xs mt-1 text-[var(--lala-blue)]">
                        Revisado por {d.reviewed_by}: <strong>{d.evaluator_status}</strong> · {d.evaluator_score?.toFixed?.(1)}/10
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border"
                      style={{ background: meta.bg, color: meta.color, borderColor: meta.color }}
                    >
                      <Icon size={12} /> {meta.label}
                    </span>
                    <div className="flex gap-1.5">
                      <button onClick={() => setReviewing(d)} className="text-[var(--lala-blue)] text-xs hover:underline font-semibold" data-testid={`supplier-doc-review-${d.id}`}>
                        Revisar →
                      </button>
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {reviewing && (
        <ReviewModal doc={reviewing} onClose={() => setReviewing(null)} onSaved={reload} />
      )}
    </div>
  );
}
